/* src/mock/data.js — dummy dataset for the offline demo run.
 * NOT part of the recovered app. Fabricated sample data shaped to mirror the real
 * go.lacak.io /#/online/trackers screen (a "Main group (71)" of devices labelled by
 * IMEI or name, a mix of ONLINE units (live status dot + moving/parked telemetry) and
 * IDLE units (clock icon, no recent fix). Used only when VITE_MOCK=1 so the rebuilt
 * monitoring screen can be exercised without a live api.lacak.io session.
 * Shapes follow the Navixy API v2 envelopes the engines read.
 */

// --- authenticated user (user/get_info -> applyUserInfo) ---
export const USER_INFO = {
  user_info: {
    id: 1,
    login: 'demo@lacak.io',
    first_name: 'Demo',
    last_name: 'Operator',
    paas_id: 42,
    balance: 1875000,
    currency: 'IDR',
    locale: 'en',
    time_zone: 'Asia/Jakarta',
    demo: true
  },
  paas_settings: { locale: 'en', paas_info: { id: 42, name: 'lacak.io' } },
  tariff_restrictions: { allowed_maps: ['osm', 'google'] },
  master: null,
  privileges: null
};

// --- roster labels (mirror the real screen: IMEIs, short codes, Indonesian names) ---
const ONLINE_LABELS = [
  '01-AB1', '5678', '350317177558446', '350544500441137', '352625690001456',
  '353742375079381', '359632107577659', '359633109022496', '860796050933516',
  '866344055693845', 'Ahsanu', 'Aji', 'Arya', 'Budi Santoso', 'Cahyo',
  'Dimas Patrol', 'Eka Kurnia', 'Fajar', 'Gilang', 'Hendra',
  'Indra Wijaya', 'Joko', 'Kurniawan', 'Lukman', 'Maulana',
  'Nanda', 'Oki', 'Putra Jaya'
];
const IDLE_LABELS = [
  '359832104655282', 'Amanah Morowali', 'Azis Jabodetabek', '862193043118762',
  '868020030114455', 'Rahmat', 'Sigit Nugroho', 'Taufik', 'Umar', 'Vino',
  'Wahyu Setiawan', 'Yusuf', 'Zaki', '353201234567890', '357654321098765',
  'Bagus', 'Candra', 'Dedi Kurnia', 'Erwin', 'Firman',
  'Galih', 'Hadi', 'Ilham', 'Krisna', 'Lestari Fleet',
  'Mahesa', 'Novita', 'Prasetyo', 'Qomar', 'Reza',
  'Surya Logistik', 'Tomi', 'Usman', 'Vicky', 'Widodo',
  'Yanto', 'Zulkifli', 'B 1234 PJR', 'B 5678 QDR', 'D 9012 AMB',
  'L 3456 PJR', 'AB 7788 PJR', 'DK 2468 AMB'
];

// deterministic IMEI-ish source id per index (no RNG so ids stay stable across polls)
function srcId(i) { return 5000 + i; }

const MODELS = ['Teltonika FMB920', 'Concox GT06N', 'Queclink GV75', 'Ruptela FM-Eco4', 'Teltonika FMC130'];

// Tracker groups (tracker/group/list). group_id 0 = "Main group" (default).
export const GROUPS = [
  { id: 0, title: 'Main group', color: '17a2b8' },
  { id: 1, title: 'Post Sales', color: '8e44ad' },
  { id: 2, title: 'CK BMB', color: 'e67e22' },
  { id: 3, title: 'Jakarta Ops', color: '2980b9' },
  { id: 4, title: 'Reserve Unit', color: '27ae60' }
];
// Deterministic group assignment: most in Main group, a handful in the others.
function groupFor(i) {
  if (i % 11 === 4) return 1
  if (i % 13 === 6) return 2
  if (i % 7 === 3) return 3
  if (i % 17 === 9) return 4
  return 0
}

const TAG_POOL = ['jakarta', 'bandung', 'patrol', 'delivery', 'vip', 'cold-chain', 'rental', 'night-shift', 'reserve'];
function makeTracker(label, i, blocked) {
  const sid = srcId(i);
  return {
    id: 100 + i,
    label,
    group_id: groupFor(i),
    // `source` shape VERBATIM from tracker/list (real API capture).
    source: {
      status_listing_id: null,
      id: sid,
      device_id: String(860000000000000 + sid),
      blocked: !!blocked,
      tariff_id: 1,
      creation_date: '2021-09-28',
      model: MODELS[i % MODELS.length],
      tariff_end_date: '2026-08-01',
      phone: '62811' + (1000000 + i)
    },
    tag_bindings: [],
    clone: false,
    // asset kind — drives which detail widgets show in the panel (the real app gates the
    // driver widget on a vehicle/fleet tariff and work-status on an employee assignment).
    // Heuristic for the demo: labels with a digit (IMEIs, plates) are vehicles; plain names
    // are employees. A couple carry an odometer-less model so the odometer widget hides.
    assetType: /\d/.test(label) ? 'vehicle' : 'employee',
    model_features: { has_odometer: i % 6 !== 5, has_engine_hours: i % 4 !== 3 },
    // scaffold-only: searchable tags for the quick-search demo (real tags resolve via
    // tag/list + tag_bindings; the live demo roster has none).
    tags: [TAG_POOL[i % TAG_POOL.length], TAG_POOL[(i * 3 + 2) % TAG_POOL.length]].filter((v, k, a) => a.indexOf(v) === k)
  };
}

// Build the full 71-device roster: online first, then idle, with one blocked unit.
// Every tracker carries a connection_status — the value that drives the row's status dot
// (active/idle/signal_lost/offline per the real tracker-tile; NOT movement_status).
const IDLE_CONN = ['idle', 'offline', 'idle', 'signal_lost', 'offline', 'idle', 'signal_lost'];
export const TRACKERS = [];
ONLINE_LABELS.forEach((l, i) => { const t = makeTracker(l, i, false); t.connection_status = 'active'; TRACKERS.push(t); });
IDLE_LABELS.forEach((l, i) => {
  const blocked = l === 'AB 7788 PJR';
  const t = makeTracker(l, ONLINE_LABELS.length + i, blocked);
  t.connection_status = blocked ? 'blocked' : IDLE_CONN[i % IDLE_CONN.length];
  TRACKERS.push(t);
});

// online tracker ids (get live telemetry + map markers); the rest are idle (clock icon).
const ONLINE_IDS = TRACKERS.slice(0, ONLINE_LABELS.length).map((t) => t.id);
export const BLOCKED = TRACKERS.filter((t) => t.source.blocked).reduce((a, t) => { a[t.id] = true; return a; }, {});

// --- movement simulation (only ONLINE units) -----------------------------
// Seed each online unit around greater Jakarta; ~55% start moving.
const JKT = { lat: -6.2146, lng: 106.8451 };
const SIM = {};
ONLINE_IDS.forEach((id, i) => {
  const ring = 0.02 + (i % 7) * 0.012;
  const ang = (i * 47) % 360;
  SIM[id] = {
    lat: JKT.lat + ring * Math.cos((ang * Math.PI) / 180),
    lng: JKT.lng + ring * Math.sin((ang * Math.PI) / 180),
    heading: (i * 63) % 360,
    speed: i % 9 < 5 ? 20 + ((i * 7) % 45) : 0,
    moving: i % 9 < 5,
    // persistent per-tracker meters + telemetry for the widgets panel
    odo: 12000 + ((i * 3797) % 88000),          // odometer km
    engineHours: 800 + ((i * 137) % 6000),       // engine hours
    battery: 60 + ((i * 13) % 40),               // %
    fuel: 25 + ((i * 17) % 70),                  // % fuel-sensor
    temp: 60 + ((i * 7) % 40),                   // engine °C
    output1: false                               // relay/engine-block output
  };
});

function advance(s) {
  if (Math.random() < 0.12) s.moving = !s.moving;
  if (s.moving) {
    if (s.speed < 5) s.speed = 25 + Math.round(Math.random() * 40);
    s.speed = Math.max(8, Math.min(90, s.speed + Math.round((Math.random() - 0.5) * 16)));
    s.heading = (s.heading + (Math.random() - 0.5) * 40 + 360) % 360;
    const distKm = (s.speed / 3600) * 10;
    const rad = (s.heading * Math.PI) / 180;
    s.lat += (distKm / 111) * Math.cos(rad);
    s.lng += (distKm / (111 * Math.cos((s.lat * Math.PI) / 180))) * Math.sin(rad);
    // accumulate meters + burn fuel while driving
    s.odo += distKm;
    s.engineHours += 10 / 3600;
    s.fuel = Math.max(5, s.fuel - distKm * 0.05);
    s.temp = Math.min(105, s.temp + 0.3);
  } else {
    s.speed = 0;
    s.temp = Math.max(30, s.temp - 0.5);
  }
  return s;
}

// Build one tracker/get_states `states[id]` entry. Shape is VERBATIM from the real API
// (captured live from go.lacak.io): gps.speed/heading are gps-level siblings of location
// (NOT inside it); battery_level is a number; gsm is {signal_level,network_name,roaming};
// inputs/outputs are arrays of booleans.
function toState(trackerId, sourceId, s) {
  const now = new Date().toISOString();
  return {
    source_id: sourceId,
    gps: {
      updated: now,
      signal_level: 70 + Math.round(Math.random() * 30),          // GPS satellite signal
      location: { lat: Number(s.lat.toFixed(7)), lng: Number(s.lng.toFixed(7)) },
      heading: Math.round(s.heading),
      speed: Math.round(s.speed),
      precision: 4 + Math.round(Math.random() * 16),
      alt: 0
    },
    connection_status: 'active',
    // moving → arrow marker; non-moving split between 'stopped' (square) and 'parked'
    // (circle + "P") so both real marker shapes are represented.
    movement_status: s.moving ? 'moving' : (Number(trackerId) % 2 === 0 ? 'stopped' : 'parked'),
    last_update: now,
    gsm: { updated: now, signal_level: 45 + Math.round(Math.random() * 55), network_name: 'Telkomsel', roaming: false },
    battery_level: Math.round(s.battery),
    battery_update: now,
    inputs: [!!s.moving, false, Number(trackerId) % 3 === 0],     // [in1, in2, in3] booleans
    inputs_update: now,
    outputs: [!!s.output1],                                       // [out1] boolean
    outputs_update: now,
    actual_track_update: now,
    // --- demo-only extras (in the real app these come from other endpoints: counters via
    //     tracker/get_counters, sensors/work_status via status/tracker/list) ---
    counters: { odometer: Math.round(s.odo), engine_hours: Math.round(s.engineHours) },
    sensors: [
      { name: 'Fuel level', value: Math.round(s.fuel), units: '%' },
      { name: 'Engine temp', value: Math.round(s.temp), units: '°C' }
    ],
    work_status: s.moving ? 'On the way' : 'Idling'
  };
}
// mutate an output (tracker/output/set) — used by the outputs widget write path.
// Only online (simulated) units are controllable; others report failure rather than a
// silent false-success so the widget can reflect that the command didn't take.
export function setOutput(trackerId, on) {
  if (!SIM[trackerId]) return { success: false, status: { code: 201, description: 'Tracker is not connected' } };
  SIM[trackerId].output1 = !!on;
  return { success: true };
}

// --- historical tracks (track/read) for the history-playback bar ---
// Cache a synthetic route per (tracker, window) so playback is stable within a session.
const TRACKS = {};
// hours = window size (drives the point count); from/to = the requested window (ISO strings).
// The route SHAPE is deterministic per (tracker, duration); the point TIMES are anchored to the
// requested `from` (or now-minus-window if absent) and spread across the window — so two
// different date windows of the same duration cache separately and each reports times that match
// its own header (no stale-cache cross-window mismatch).
export function buildTrack(trackerId, hours, from, to) {
  const h = hours || 3;
  const key = trackerId + ':' + h + ':' + (from || '') + ':' + (to || '');
  if (TRACKS[key]) return TRACKS[key];
  const seed = (trackerId * 2654435761 + h * 7919) % 2147483647;
  // Integer-state LCG. (The previous version kept the state as a float in [0,1), which
  // made the recurrence converge to a fixed point — every point got the same speed.)
  let s = (seed % 233280 + 233280) % 233280;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const base = SIM[trackerId] || { lat: JKT.lat, lng: JKT.lng };
  let lat = base.lat - 0.03 + rand() * 0.02;
  let lng = base.lng - 0.03 + rand() * 0.02;
  let heading = rand() * 360;
  const N = Math.min(400, Math.max(30, Math.round(h * 30))); // ~30 points/hour, capped
  const fromMs = from ? new Date(from).getTime() : NaN;
  const toMs = to ? new Date(to).getTime() : NaN;
  const start = Number.isFinite(fromMs) ? fromMs : (Date.now() - h * 3600 * 1000);
  const span = (Number.isFinite(fromMs) && Number.isFinite(toMs) && toMs > fromMs) ? (toMs - fromMs) : (h * 3600 * 1000);
  const stepMs = span / N;
  const points = [];
  // A mid-track run where GPS drops to cell-tower/LBS positioning — coarse fixes (precision > 400)
  // the history draws as a dashed grey "no data" segment + accuracy circles. Deterministic.
  const coarseStart = Math.floor(N * 0.46);
  const coarseEnd = coarseStart + Math.max(4, Math.round(N * 0.05));
  let mileageKm = 0;
  for (let i = 0; i < N; i++) {
    const moving = rand() > 0.15;
    const speed = moving ? 15 + Math.round(rand() * 55) : 0;
    if (moving) {
      heading = (heading + (rand() - 0.5) * 50 + 360) % 360;
      const distKm = (speed / 3600) * (stepMs / 1000);
      const rad = (heading * Math.PI) / 180;
      lat += (distKm / 111) * Math.cos(rad);
      lng += (distKm / (111 * Math.cos((lat * Math.PI) / 180))) * Math.sin(rad);
      mileageKm += distKm;
    }
    const coarse = i >= coarseStart && i < coarseEnd;
    points.push({
      lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)),
      speed, heading: Math.round(heading),
      time: new Date(start + i * stepMs).toISOString(),
      // metres of GPS accuracy — fine fixes 4–40 m; the LBS run is coarse (550–1250 m).
      precision: coarse ? 550 + Math.round(rand() * 700) : 4 + Math.round(rand() * 36),
      gsm_lbs: coarse,
      mileage: Number(mileageKm.toFixed(2))
    });
  }
  TRACKS[key] = points;
  return points;
}

// --- history events (history/tracker/list) for the travel-history EVENTS tab ---
// Derived from the SAME synthetic track (so each event has a real time + location that lines
// up with a track point → clicking it can seek playback). `type` uses the VERBATIM go.lacak.io
// event vocabulary (notifications.events.*): online, offline, inzone, outzone, poweron,
// poweroff, info. Deterministic + cached per (tracker, window), like buildTrack.
const EVENTS = {};
export function buildEvents(trackerId, hours, from, to) {
  const h = hours || 3;
  const key = trackerId + ':' + h + ':' + (from || '') + ':' + (to || '');
  if (EVENTS[key]) return EVENTS[key];
  const pts = buildTrack(trackerId, h, from, to);
  const n = pts.length;
  if (!n) { EVENTS[key] = []; return EVENTS[key]; }
  const at = (frac) => pts[Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1))))];
  const ev = (id, type, frac, message) => { const p = at(frac); return { id, tracker_id: trackerId, type, time: p.time, message, location: { lat: p.lat, lng: p.lng }, read: id % 2 === 0 }; };
  // the fastest fix in the window → a realistic "info" (speeding) event
  let fi = 0; let fastest = 0;
  pts.forEach((p, i) => { if ((p.speed || 0) > fastest) { fastest = p.speed || 0; fi = i; } });
  const list = [
    ev(1, 'poweron', 0, 'Ignition on'),
    ev(2, 'inzone', 0.18, 'Entered zone: Jakarta CBD'),
    { id: 3, tracker_id: trackerId, type: 'info', time: pts[fi].time, message: 'Speeding: ' + fastest + ' km/h', location: { lat: pts[fi].lat, lng: pts[fi].lng }, read: false },
    ev(4, 'outzone', 0.42, 'Exited zone: Jakarta CBD'),
    ev(5, 'offline', 0.60, 'Connection lost'),
    ev(6, 'online', 0.66, 'Connection restored'),
    ev(7, 'inzone', 0.85, 'Entered zone: Depot North'),
    ev(8, 'poweroff', 1, 'Ignition off')
  ];
  list.sort((a, b) => new Date(a.time) - new Date(b.time));
  EVENTS[key] = list;
  return list;
}

// tracker.id -> source.id, for every tracker (online and idle)
const SRC_OF = TRACKERS.reduce((a, t) => { a[t.id] = t.source.id; return a; }, {});

// --- custom map layers (map_layer/list) — mirrors the real "Layers" panel ---
// Each is a user-defined overlay; `bounds` drives the demo rectangle we draw when toggled.
export const MAP_LAYERS = [
  { id: 1, title: 'Bekasi City (Administrative Area)', type: 'area', color: '#8e44ad', bounds: [[-6.32, 106.95], [-6.20, 107.08]] },
  { id: 2, title: 'KM', type: 'area', color: '#16a085', bounds: [[-6.16, 106.78], [-6.10, 106.86]] },
  { id: 3, title: 'Toll Gates', type: 'poi', color: '#e67e22', bounds: [[-6.24, 106.80], [-6.18, 106.90]] },
  { id: 4, title: 'Unilever Cold Storage (Jakarta Area)', type: 'area', color: '#2980b9', bounds: [[-6.13, 106.88], [-6.07, 106.96]] },
  { id: 5, title: 'bogor', type: 'area', color: '#27ae60', bounds: [[-6.62, 106.76], [-6.54, 106.84]] },
  { id: 6, title: 'jkt1', type: 'area', color: '#c0392b', bounds: [[-6.22, 106.80], [-6.16, 106.86]] },
  { id: 7, title: 'Drone', type: 'raster', color: '#34495e', bounds: [[-6.20, 106.82], [-6.14, 106.90]] },
  { id: 8, title: 'Mapbox', type: 'raster', color: '#2c3e50', bounds: [[-6.28, 106.78], [-6.20, 106.88]] }
]

// Saved places / POIs (place/list) — rendered on the map as labelled place pins with a
// radius circle. Real shape: {id, label, location:{lat,lng,address}, radius, color}.
export const PLACES = [
  { id: 1, label: 'HQ Warehouse', location: { lat: -6.1850, lng: 106.8300, address: 'Jl. Gunung Sahari, Central Jakarta' }, radius: 150, color: '#8e44ad' },
  { id: 2, label: 'Depot Bekasi', location: { lat: -6.2380, lng: 106.9750, address: 'Bekasi Regency' }, radius: 220, color: '#16a085' },
  { id: 3, label: 'Client Site — Sudirman', location: { lat: -6.2100, lng: 106.8200, address: 'Sudirman CBD, Jakarta' }, radius: 120, color: '#e67e22' },
  { id: 4, label: 'Port Tanjung Priok', location: { lat: -6.1050, lng: 106.8800, address: 'Tanjung Priok, North Jakarta' }, radius: 300, color: '#c0392b' },
  { id: 5, label: 'Rest Area KM 19', location: { lat: -6.2900, lng: 106.9900, address: 'Jakarta–Cikampek Toll' }, radius: 130, color: '#2980b9' }
]

export function buildStates() {
  const out = {};
  ONLINE_IDS.forEach((id) => {
    if (BLOCKED[id]) return;
    advance(SIM[id]);
    out[id] = toState(id, SRC_OF[id], SIM[id]);
  });
  return out;
  // idle units are absent from states -> the tile renders a clock (access_time) icon.
}
